import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { safeUrl } from '../lib/safeUrl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePlanGate, planGateFromResponse } from '../lib/planGate';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { buildCountryGate } from '../lib/countryGate';
import PlanLimitDialog from '../components/dialogs/PlanLimitDialog';
import LinkedItemsPopover from '../components/LinkedItemsPopover';
import { getIndustryLabel, projectSectorsOf } from '../constants/industries'; // WO-73
import IndustryMultiSelectFilter from '../components/IndustryMultiSelectFilter';
import { formatYear } from '../components/YearPicker';
import AdminProjectDialog from '../components/dialogs/AdminProjectDialog';
import EntityCardActions from '../components/EntityCardActions';
import LinkedEntityFilter from '../components/LinkedEntityFilter';
import useFavorites from '../hooks/useFavorites';
import useListStateCache from '../hooks/useListStateCache';
import { trackSearch, trackFeature } from '../services/eventTracker';
import { toast } from 'sonner';
import { OnboardingEmptyState } from '../onboarding';
import { REGIONS_BY_COUNTRY } from '../data/regions';
import { 
  Building2, Search, Globe, MapPin, DollarSign, 
  ChevronLeft, ChevronRight, Users, TrendingUp,
  RefreshCw, Briefcase, ArrowRight,
  ChevronDown, X, Factory, Plus, ArrowLeft, Download, FileJson, FileSpreadsheet, FileText,
  Navigation, User, Lock, Heart, CalendarDays, SlidersHorizontal, Star, Newspaper,
  LayoutGrid, List
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const countryFlags = {
  SA: '🇸🇦', AE: '🇦🇪', OM: '🇴🇲', KW: '🇰🇼', BH: '🇧🇭', QA: '🇶🇦'
};

const countryNames = {
  en: { SA: 'Saudi Arabia', AE: 'UAE', OM: 'Oman', KW: 'Kuwait', BH: 'Bahrain', QA: 'Qatar' },
  ar: { SA: 'السعودية', AE: 'الإمارات', OM: 'عمان', KW: 'الكويت', BH: 'البحرين', QA: 'قطر' }
};

const stageColors = {
  concept: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
  design: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  tender: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  execution: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  complete: 'bg-gray-500/10 text-gray-500 border-gray-500/30'
};

const stageProgress = {
  concept: 10,
  design: 30,
  tender: 50,
  execution: 75,
  complete: 100
};

// Multi-select filter component
const MultiSelectFilter = ({ label, icon: Icon, options, selected, onChange, language, isRTL, testId, disabled, disabledHint, onLockedClick }) => {
  const [open, setOpen] = useState(false);
  
  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const clearAll = () => onChange([]);
  const selectAll = () => onChange(options.map(o => o.value));

  return (
    <Popover open={disabled ? false : open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className={`h-11 px-3 justify-between min-w-[160px] ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={disabled}
          title={disabled ? disabledHint : undefined}
          data-testid={testId}
        >
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-muted-foreground" />
            <span className="truncate max-w-[100px]">
              {selected.length === 0 
                ? label 
                : selected.length === 1 
                  ? options.find(o => o.value === selected[0])?.label || selected[0]
                  : `${selected.length} ${language === 'ar' ? 'محدد' : 'selected'}`
              }
            </span>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <div className="p-2 border-b flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={selectAll} className="h-7 text-xs">
            {language === 'ar' ? 'تحديد الكل' : 'Select All'}
          </Button>
          <Button variant="ghost" size="sm" onClick={clearAll} className="h-7 text-xs">
            {language === 'ar' ? 'مسح' : 'Clear'}
          </Button>
        </div>
        <ScrollArea className="h-[250px]">
          <div className="p-2 space-y-1">
            {options.map((option) => (
              <div
                key={option.value}
                className={`flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer hover:bg-muted transition-colors ${
                  option.locked ? 'opacity-70' : selected.includes(option.value) ? 'bg-primary/10' : ''
                }`}
                onClick={() => (option.locked ? onLockedClick?.(option.value) : toggleOption(option.value))}
              >
                <Checkbox
                  checked={!option.locked && selected.includes(option.value)}
                  onCheckedChange={() => (option.locked ? onLockedClick?.(option.value) : toggleOption(option.value))}
                  disabled={option.locked}
                  className="pointer-events-none"
                />
                {option.icon && <span>{option.icon}</span>}
                <span className="text-sm flex-1">{option.label}</span>
                {option.locked && (
                  <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 whitespace-nowrap" data-testid={`locked-country-${option.value}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    {language === 'ar' ? 'ستاندرد' : 'Standard'}
                  </span>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
        {selected.length > 0 && (
          <div className="p-2 border-t">
            <div className="flex flex-wrap gap-1">
              {selected.slice(0, 3).map((val) => {
                const opt = options.find(o => o.value === val);
                return (
                  <Badge key={val} variant="secondary" className="text-xs">
                    {opt?.icon} {opt?.label?.slice(0, 10)}
                    <X 
                      className="w-3 h-3 ml-1 cursor-pointer" 
                      onClick={(e) => { e.stopPropagation(); toggleOption(val); }}
                    />
                  </Badge>
                );
              })}
              {selected.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{selected.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

// GCC regions flat list grouped by country for the region filter
const GCC_COUNTRIES = ['SA', 'AE', 'QA', 'KW', 'BH', 'OM'];
const GCC_REGION_OPTIONS = GCC_COUNTRIES.flatMap(code =>
  (REGIONS_BY_COUNTRY[code] || []).map(r => ({ value: r, label: r, countryCode: code }))
);

// Progress range filter
const ProgressRangeFilter = ({ minVal, maxVal, onChange, language, testId }) => {
  const [open, setOpen] = useState(false);
  const [localMin, setLocalMin] = useState(minVal ?? '');
  const [localMax, setLocalMax] = useState(maxVal ?? '');

  const apply = () => {
    onChange(localMin === '' ? null : Number(localMin), localMax === '' ? null : Number(localMax));
    setOpen(false);
  };
  const clear = () => {
    setLocalMin(''); setLocalMax('');
    onChange(null, null);
    setOpen(false);
  };

  const label = (() => {
    if (minVal !== null && maxVal !== null) return `${minVal}% – ${maxVal}%`;
    if (minVal !== null) return `≥ ${minVal}%`;
    if (maxVal !== null) return `≤ ${maxVal}%`;
    return language === 'ar' ? 'التقدم' : 'Progress';
  })();

  const isActive = minVal !== null || maxVal !== null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`h-11 px-3 justify-between min-w-[140px] ${isActive ? 'border-primary text-primary' : ''}`}
          data-testid={testId}
        >
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
            <span className="truncate max-w-[100px] text-sm">{label}</span>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-4" align="start">
        <p className="text-sm font-medium mb-3">{language === 'ar' ? 'نطاق التقدم (%)' : 'Progress Range (%)'}</p>
        <div className="flex items-center gap-2 mb-4">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground mb-1 block">{language === 'ar' ? 'من' : 'Min'}</label>
            <Input
              type="number" min={0} max={100} value={localMin}
              onChange={e => setLocalMin(e.target.value)}
              placeholder="0" className="h-9"
              data-testid="progress-min-input"
            />
          </div>
          <span className="text-muted-foreground mt-4">–</span>
          <div className="flex-1">
            <label className="text-xs text-muted-foreground mb-1 block">{language === 'ar' ? 'إلى' : 'Max'}</label>
            <Input
              type="number" min={0} max={100} value={localMax}
              onChange={e => setLocalMax(e.target.value)}
              placeholder="100" className="h-9"
              data-testid="progress-max-input"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={apply} className="flex-1 h-8">{language === 'ar' ? 'تطبيق' : 'Apply'}</Button>
          <Button size="sm" variant="outline" onClick={clear} className="h-8">{language === 'ar' ? 'مسح' : 'Clear'}</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Date range filter — month/year with quick-select presets and direct year editing
const DateRangeFilter = ({ label, icon: Icon, fromVal, toVal, onChange, language, testId }) => {
  const currentYear = new Date().getFullYear();

  const detectMode = (from, to) => (!from && !to ? 'any' : 'custom');

  const [open, setOpen] = useState(false);
  const [quickMode, setQuickMode] = useState(() => detectMode(fromVal, toVal));
  const [viewYear, setViewYear] = useState(() => (fromVal ? parseInt(fromVal) : currentYear));
  const [localFrom, setLocalFrom] = useState(fromVal || '');
  const [localTo, setLocalTo] = useState(toVal || '');
  const [pickStep, setPickStep] = useState('from');

  const handleOpenChange = (isOpen) => {
    if (isOpen) {
      const mode = detectMode(fromVal, toVal);
      setQuickMode(mode);
      setLocalFrom(fromVal || '');
      setLocalTo(toVal || '');
      setPickStep('from');
      if (fromVal) setViewYear(parseInt(fromVal));
    }
    setOpen(isOpen);
  };

  const QUICK_OPTS = [
    { id: 'any',    label: language === 'ar' ? 'أي تاريخ'    : 'Any Date',      from: '', to: ''   },
    { id: 'custom', label: language === 'ar' ? 'نطاق مخصص'   : 'Custom Range',  from: null, to: null },
  ];

  const handleQuickSelect = (opt) => {
    setQuickMode(opt.id);
    if (opt.id === 'any') {
      setLocalFrom(''); setLocalTo('');
      onChange(null, null);
      setOpen(false);
    } else if (opt.id !== 'custom') {
      setLocalFrom(opt.from); setLocalTo(opt.to);
      onChange(opt.from, opt.to);
      setOpen(false);
    } else {
      // Stay open to pick custom range
      setLocalFrom(''); setLocalTo('');
      setPickStep('from');
    }
  };

  const handleYearClick = (year) => {
    const y = String(year);
    if (pickStep === 'from') {
      setLocalFrom(y);
      setLocalTo('');
      setPickStep('to');
    } else {
      const [finalFrom, finalTo] = y >= localFrom ? [localFrom, y] : [y, localFrom];
      setLocalFrom(finalFrom);
      setLocalTo(finalTo);
      onChange(finalFrom, finalTo);
      setOpen(false);
    }
  };

  const getYearState = (year) => {
    const y = String(year);
    if (y === localFrom) return 'from';
    if (y === localTo) return 'to';
    if (localFrom && localTo && y > localFrom && y < localTo) return 'range';
    return 'none';
  };

  const displayLabel = (() => {
    if (!fromVal && !toVal) return label;
    if (fromVal && toVal) {
      // Show compact label like "2026 – 2027" if full years, else full "YYYY-MM"
      if (fromVal.endsWith('-01') && toVal.endsWith('-12') && fromVal.slice(0,4) === toVal.slice(0,4))
        return fromVal.slice(0,4);
      return `${fromVal} – ${toVal}`;
    }
    if (fromVal) return `≥ ${fromVal}`;
    return `≤ ${toVal}`;
  })();

  const isActive = fromVal || toVal;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`h-11 px-3 justify-between min-w-[150px] ${isActive ? 'border-primary text-primary' : ''}`}
          data-testid={testId}
        >
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-muted-foreground" />
            <span className="truncate max-w-[120px] text-sm">{displayLabel}</span>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 shadow-lg" align="start">
        <div className="flex">
          {/* Quick Select panel */}
          <div className="w-44 border-r p-2 space-y-0.5">
            <p className="text-[11px] font-semibold text-muted-foreground px-2 py-1.5 uppercase tracking-wider">
              {language === 'ar' ? 'اختيار سريع' : 'Quick Select'}
            </p>
            {QUICK_OPTS.map(opt => (
              <button
                key={opt.id}
                onClick={() => handleQuickSelect(opt)}
                className={`w-full text-left px-2 py-2 rounded-md text-sm transition-colors flex items-center gap-1.5 ${
                  quickMode === opt.id
                    ? 'bg-primary/10 text-primary font-semibold'
                    : 'hover:bg-muted text-foreground'
                }`}
              >
                <span className="w-3 text-xs shrink-0">{quickMode === opt.id ? '✓' : ''}</span>
                {opt.label}
              </button>
            ))}
          </div>

          {/* Year-grid panel (Custom Range only) */}
          {quickMode === 'custom' && (
            <div className="p-4 w-60">
              {/* Instruction */}
              <p className="text-xs text-muted-foreground mb-3 font-medium">
                {pickStep === 'from'
                  ? (language === 'ar' ? 'اختر سنة البداية' : 'Select start year')
                  : (language === 'ar' ? 'اختر سنة النهاية' : 'Select end year')}
                {localFrom && pickStep === 'to' && (
                  <span className="ml-1 font-semibold text-primary">{localFrom}</span>
                )}
              </p>

              {/* Decade nav */}
              <div className="flex items-center justify-between mb-3">
                <button onClick={() => setViewYear(y => y - 12)} className="p-1 rounded hover:bg-muted transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="font-bold text-sm">
                  {viewYear - 5} – {viewYear + 6}
                </span>
                <button onClick={() => setViewYear(y => y + 12)} className="p-1 rounded hover:bg-muted transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Year grid 4×3 */}
              <div className="grid grid-cols-4 gap-1">
                {Array.from({ length: 12 }, (_, i) => (viewYear - 5) + i).map((yr) => {
                  const state = getYearState(yr);
                  return (
                    <button
                      key={yr}
                      onClick={() => handleYearClick(yr)}
                      data-testid={`${testId}-year-${yr}`}
                      className={`py-2 text-xs rounded-md font-medium transition-colors ${
                        state === 'from' || state === 'to'
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : state === 'range'
                            ? 'bg-primary/15 text-primary'
                            : 'hover:bg-muted text-foreground'
                      }`}
                    >
                      {yr}
                    </button>
                  );
                })}
              </div>

              {/* Range summary + clear */}
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {localFrom ? `${localFrom} → ${localTo || '…'}` : (language === 'ar' ? 'لم يُحدَّد' : 'Not set')}
                </span>
                <Button
                  size="sm" variant="ghost"
                  className="h-7 text-xs text-muted-foreground hover:text-destructive px-2"
                  onClick={() => { setLocalFrom(''); setLocalTo(''); setPickStep('from'); onChange(null, null); }}
                >
                  {language === 'ar' ? 'مسح' : 'Clear'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Estimated Value range filter
const ValueRangeFilter = ({ minVal, maxVal, onChange, language, testId }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('more');
  const [minInput, setMinInput] = useState('');
  const [maxInput, setMaxInput] = useState('');

  const toNum  = (val) => { const n = parseFloat(val); return isNaN(n) || n <= 0 ? null : n; };
  const fromNum = (n)  => n == null ? '' : String(n / 1_000_000);
  const fmtLabel = (n) => n == null ? '' : `$${(n / 1_000_000).toLocaleString()}M`;

  const displayLabel = (() => {
    if (minVal != null && maxVal != null) return `${fmtLabel(minVal)} – ${fmtLabel(maxVal)}`;
    if (minVal != null) return `> ${fmtLabel(minVal)}`;
    if (maxVal != null) return `< ${fmtLabel(maxVal)}`;
    return language === 'ar' ? 'القيمة' : 'Est. Value';
  })();

  const isActive = minVal != null || maxVal != null;

  const handleOpenChange = (isOpen) => {
    if (isOpen) {
      if (minVal != null && maxVal != null) {
        setMode('range'); setMinInput(fromNum(minVal)); setMaxInput(fromNum(maxVal));
      } else if (minVal != null) {
        setMode('more'); setMinInput(fromNum(minVal)); setMaxInput('');
      } else if (maxVal != null) {
        setMode('less'); setMaxInput(fromNum(maxVal)); setMinInput('');
      } else {
        setMode('more'); setMinInput(''); setMaxInput('');
      }
    }
    setOpen(isOpen);
  };

  const apply = () => {
    let min = null, max = null;
    if (mode === 'more') min = toNum(minInput);
    else if (mode === 'less') max = toNum(maxInput);
    else { min = toNum(minInput); max = toNum(maxInput); }
    onChange(min, max);
    setOpen(false);
  };

  const clear = () => { setMinInput(''); setMaxInput(''); onChange(null, null); setOpen(false); };

  const PRESETS = [
    { label: '$10M',    v: 10    },
    { label: '$50M',    v: 50    },
    { label: '$100M',   v: 100   },
    { label: '$500M',   v: 500   },
    { label: '$1,000M', v: 1_000 },
  ];

  const applyPreset = (vM) => {
    if (mode === 'less') setMaxInput(String(vM));
    else setMinInput(String(vM));
  };

  const MODES = [
    { id: 'more',  label: language === 'ar' ? 'أكثر من' : 'More than' },
    { id: 'range', label: language === 'ar' ? 'نطاق'    : 'Range'     },
    { id: 'less',  label: language === 'ar' ? 'أقل من'  : 'Less than' },
  ];

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline"
          className={`h-11 px-3 justify-between min-w-[145px] ${isActive ? 'border-primary text-primary' : ''}`}
          data-testid={testId}>
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-muted-foreground" />
            <span className="truncate max-w-[110px] text-sm">{displayLabel}</span>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-4" align="start">
        <p className="text-sm font-medium mb-3">{language === 'ar' ? 'القيمة التقديرية' : 'Estimated Value'}</p>

        {/* Mode tabs */}
        <div className="flex rounded-lg border overflow-hidden mb-4">
          {MODES.map(m => (
            <button key={m.id} onClick={() => setMode(m.id)}
              className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                mode === m.id ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-foreground'
              }`}>
              {m.label}
            </button>
          ))}
        </div>

        {/* Quick preset chips — all in M */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {PRESETS.map(p => (
            <button key={p.label} onClick={() => applyPreset(p.v)}
              className="px-2.5 py-1 text-xs rounded-full border hover:bg-primary/10 hover:border-primary hover:text-primary transition-colors">
              {p.label}
            </button>
          ))}
        </div>

        {/* Inputs — value is in millions, suffix "M" is shown as static text */}
        {mode === 'range' ? (
          <div className="space-y-2 mb-4">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">{language === 'ar' ? 'من (مليون $)' : 'Min (Mill. USD)'}</label>
              <div className="flex items-center gap-1">
                <Input type="number" min={0} value={minInput} onChange={e => setMinInput(e.target.value)}
                  placeholder="0" className="h-9 flex-1" data-testid="value-min-input" />
                <span className="text-sm font-medium text-muted-foreground w-6">M</span>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">{language === 'ar' ? 'إلى (مليون $)' : 'Max (Mill. USD)'}</label>
              <div className="flex items-center gap-1">
                <Input type="number" min={0} value={maxInput} onChange={e => setMaxInput(e.target.value)}
                  placeholder="0" className="h-9 flex-1" data-testid="value-max-input" />
                <span className="text-sm font-medium text-muted-foreground w-6">M</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-4">
            <label className="text-xs text-muted-foreground mb-1 block">
              {mode === 'more'
                ? (language === 'ar' ? 'الحد الأدنى (مليون $)' : 'Minimum (Mill. USD)')
                : (language === 'ar' ? 'الحد الأقصى (مليون $)' : 'Maximum (Mill. USD)')}
            </label>
            <div className="flex items-center gap-1">
              <Input type="number" min={0}
                value={mode === 'more' ? minInput : maxInput}
                onChange={e => mode === 'more' ? setMinInput(e.target.value) : setMaxInput(e.target.value)}
                placeholder="0" className="h-9 flex-1"
                data-testid={mode === 'more' ? 'value-min-input' : 'value-max-input'} />
              <span className="text-sm font-medium text-muted-foreground w-6">M</span>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Button size="sm" onClick={apply} className="flex-1 h-8">{language === 'ar' ? 'تطبيق' : 'Apply'}</Button>
          <Button size="sm" variant="outline" onClick={clear} className="h-8">{language === 'ar' ? 'مسح' : 'Clear'}</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default function ProjectsPage() {
  const { language, isRTL } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterIds = (searchParams.get('filter_ids') || '').split(',').filter(Boolean);
  const isFiltered = filterIds.length > 0;
  const isAdmin = user?.role === 'admin';
  const userPlan = user?.plan_id || 'free';
  const isPremiumOrEnterprise = userPlan === 'premium' || userPlan === 'enterprise';
  const canCreateProjects = isAdmin || userPlan === 'standard' || isPremiumOrEnterprise;

  // Check if user can edit a specific project
  const canEditProject = (project) => {
    if (isAdmin) return true;
    if (canCreateProjects && project.created_by === user?.user_id) return true;
    return false;
  };
  
  const [projects, setProjects] = useState([]);
  const [allSectors, setAllSectors] = useState([]);
  const [companiesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('projects_view_mode') || 'card');
  useEffect(() => { localStorage.setItem('projects_view_mode', viewMode); }, [viewMode]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Admin CRUD state
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [deleteProject, setDeleteProject] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Extract-as-Lead state
  const [savingLeadId, setSavingLeadId] = useState(null);
  const [savedLeadIds, setSavedLeadIds] = useState(() => new Set());
  
  // Multi-select Filters
  const [search, setSearch] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedStages, setSelectedStages] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [linkedCompanyFilter, setLinkedCompanyFilter] = useState([]); // [{ _id, name }]
  const [searchInput, setSearchInput] = useState('');
  
  // Extended filters
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [progressMin, setProgressMin] = useState(null);
  const [progressMax, setProgressMax] = useState(null);
  const [valueMin, setValueMin] = useState(null);
  const [valueMax, setValueMax] = useState(null);
  const [startDateFrom, setStartDateFrom] = useState(null);
  const [startDateTo, setStartDateTo] = useState(null);
  const [endDateFrom, setEndDateFrom] = useState(null);
  const [endDateTo, setEndDateTo] = useState(null);
  const [masterProjectOnly, setMasterProjectOnly] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  
  // Use the centralized favorites hook
  const { 
    isFavorite, 
    handleFavoriteToggle, 
    filterByFavorites,
    showFavoritesOnly, 
    setShowFavoritesOnly 
  } = useFavorites('project', token);
  
  // Page size
  const PAGE_SIZE = 20;

  // Check if user can export (not free plan)
  const canExport = user?.plan_id?.toLowerCase() !== 'free';
  const planGate = usePlanGate();
  const { allowedCountries, allCountries, upgradeUrl, planId } = useEntitlements();
  const isCountryLocked = (code) => !allCountries && !allowedCountries.includes(code);
  const handleLockedCountry = (code) => planGate.show(buildCountryGate(code, planId, upgradeUrl, language));
  const [countryGate, setCountryGate] = useState(null);

  // Export handler — server returns a 402 plan_gate when the plan is too low.
  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/intelligence/projects/export?format=${format}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const pg = await planGateFromResponse(response);
        if (planGate.show(pg)) return;
        let detail = '';
        try { detail = (await response.clone().json()).detail; } catch { /* non-json */ }
        throw new Error(detail || (language === 'ar' ? 'تعذّر التصدير' : 'Export could not be completed'));
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `projects_export.${format === 'excel' ? 'xlsx' : format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=([^;]+)/);
        if (match) filename = match[1].replace(/"/g, '');
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(language === 'ar' 
        ? `تم تصدير المشاريع بنجاح (${format.toUpperCase()})`
        : `Projects exported successfully (${format.toUpperCase()})`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || (language === 'ar' ? 'فشل التصدير' : 'Export failed'));
    } finally {
      setIsExporting(false);
    }
  };

  const fetchProjects = async (isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      const currentPage = isLoadMore ? page + 1 : 1;
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: PAGE_SIZE.toString()
      });
      
      // WO-73: every chosen country, stage and sector goes to the server, which
      // filters the WHOLE database. (Before, the browser filtered only the 20
      // projects already loaded, so "Water" showed 0 of 664.)
      selectedCountries.forEach(c => params.append('country', c));
      if (linkedCompanyFilter.length > 0) linkedCompanyFilter.forEach(f => params.append('company_id', f._id));
      selectedStages.forEach(st => params.append('stage', st));
      selectedIndustries.forEach(ind => params.append('sector', ind));
      if (search) params.append('search', search);
      
      // Extended filters — sent to backend
      if (selectedRegions.length > 0) selectedRegions.forEach(r => params.append('region', r));
      if (progressMin !== null) params.append('min_progress', progressMin.toString());
      if (progressMax !== null) params.append('max_progress', progressMax.toString());
      if (valueMin !== null) params.append('value_min', valueMin.toString());
      if (valueMax !== null) params.append('value_max', valueMax.toString());
      if (startDateFrom) params.append('start_date_from', startDateFrom);
      if (startDateTo) params.append('start_date_to', startDateTo);
      if (endDateFrom) params.append('end_date_from', endDateFrom);
      if (endDateTo) params.append('end_date_to', endDateTo);
      if (masterProjectOnly) params.append('is_master_project', 'true');
      
      const response = await fetch(`${BACKEND_URL}/api/intelligence/projects?${params}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!response.ok) throw new Error('Failed to fetch projects');
      
      const data = await response.json();
      
      // Collect all unique sectors from raw data (before filtering)
      const sectors = [...new Set(data.items.flatMap(item =>
        item.sectors?.length ? item.sectors : (item.sector ? [item.sector] : [])
      ))];
      setAllSectors(prev => {
        const combined = [...new Set([...prev, ...sectors])];
        return combined.length > prev.length ? combined : prev;
      });
      
      // WO-73: the server already applied every filter.
      const filteredItems = data.items;
      
      if (isLoadMore) {
        setProjects(prev => [...prev, ...filteredItems]);
        setPage(currentPage);
      } else {
        setProjects(filteredItems);
        setPage(1);
      }
      
      setHasMore(data.page < data.total_pages);
      setTotal(data.total);
      setCountryGate(data.plan_gate || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  const loadMore = () => {
    fetchProjects(true);
  };

  useEffect(() => {
    if (isFiltered) return;
    if (lastLoadedSigRef.current === fetchSig) return; // already have data for these filters (e.g. just restored)
    lastLoadedSigRef.current = fetchSig;
    fetchProjects(false);
  }, [isFiltered, selectedCountries, selectedStages, selectedIndustries, search, linkedCompanyFilter,
      selectedRegions, progressMin, progressMax, valueMin, valueMax,
      startDateFrom, startDateTo, endDateFrom, endDateTo, masterProjectOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isFiltered || !token) return;
    const loadFiltered = async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          filterIds.map(id =>
            fetch(`${BACKEND_URL}/api/intelligence/projects/${id}`, {
              headers: { Authorization: `Bearer ${token}` }
            }).then(r => r.ok ? r.json() : null)
          )
        );
        const valid = results.filter(Boolean);
        setProjects(valid);
        setTotal(valid.length);
      } finally {
        setLoading(false);
      }
    };
    loadFiltered();
  }, [filterIds.join(','), token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-clear regions that no longer belong to selected countries
  useEffect(() => {
    if (selectedRegions.length > 0 && selectedCountries.length > 0) {
      const validRegions = new Set(selectedCountries.flatMap(code => REGIONS_BY_COUNTRY[code] || []));
      const filtered = selectedRegions.filter(r => validRegions.has(r));
      if (filtered.length !== selectedRegions.length) setSelectedRegions(filtered);
    } else if (selectedCountries.length === 0 && selectedRegions.length > 0) {
      setSelectedRegions([]);
    }
  }, [selectedCountries]);

  // Available regions derived from selected countries
  const availableRegionOptions = selectedCountries.length > 0
    ? selectedCountries.flatMap(code => (REGIONS_BY_COUNTRY[code] || []).map(r => ({ value: r, label: r })))
    : [];

  // Filter projects by favorites if toggle is on
  const displayedProjects = filterByFavorites(projects);

  // ── Restore loaded cards + filters + scroll when returning from a detail page ──
  const { restoredState, write } = useListStateCache('projects', '/dashboard/projects/');
  const fetchSig = JSON.stringify({
    isFiltered, search, selectedCountries, selectedStages, selectedIndustries, linkedCompanyFilter,
    selectedRegions, progressMin, progressMax, valueMin, valueMax,
    startDateFrom, startDateTo, endDateFrom, endDateTo, masterProjectOnly,
  });
  const lastLoadedSigRef = useRef(restoredState ? restoredState.sig : null);

  useLayoutEffect(() => {
    if (!restoredState) return;
    const f = restoredState.filters || {};
    setSearch(f.search || '');
    setSearchInput(f.searchInput || '');
    setSelectedCountries(f.selectedCountries || []);
    setSelectedStages(f.selectedStages || []);
    setSelectedIndustries(f.selectedIndustries || []);
    setLinkedCompanyFilter(f.linkedCompanyFilter || []);
    setSelectedRegions(f.selectedRegions || []);
    setProgressMin(f.progressMin ?? null);
    setProgressMax(f.progressMax ?? null);
    setValueMin(f.valueMin ?? null);
    setValueMax(f.valueMax ?? null);
    setStartDateFrom(f.startDateFrom ?? null);
    setStartDateTo(f.startDateTo ?? null);
    setEndDateFrom(f.endDateFrom ?? null);
    setEndDateTo(f.endDateTo ?? null);
    setMasterProjectOnly(!!f.masterProjectOnly);
    if (f.showFavoritesOnly) setShowFavoritesOnly(true);
    setProjects(restoredState.items || []);
    setPage(restoredState.page || 1);
    setHasMore(!!restoredState.hasMore);
    setTotal(restoredState.total || 0);
    setLoading(false);
    const y = restoredState.scrollY || 0;
    setTimeout(() => window.scrollTo(0, y), 60);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const latestStateRef = useRef({});
  latestStateRef.current = {
    items: projects, page, hasMore, total, sig: fetchSig, scrollY: 0,
    filters: {
      search, searchInput, selectedCountries, selectedStages, selectedIndustries, linkedCompanyFilter,
      selectedRegions, progressMin, progressMax, valueMin, valueMax,
      startDateFrom, startDateTo, endDateFrom, endDateTo, masterProjectOnly, showFavoritesOnly,
    },
  };
  // Persist on every change so returning from a detail page restores everything.
  useEffect(() => {
    write({ ...latestStateRef.current, scrollY: window.scrollY });
  }, [projects, page, hasMore, total, fetchSig, showFavoritesOnly, write]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const clearAllFilters = () => {
    setSelectedCountries([]);
    setSelectedStages([]);
    setSelectedIndustries([]);
    setLinkedCompanyFilter([]);
    setSelectedRegions([]);
    setProgressMin(null);
    setProgressMax(null);
    setValueMin(null);
    setValueMax(null);
    setStartDateFrom(null);
    setStartDateTo(null);
    setEndDateFrom(null);
    setEndDateTo(null);
    setMasterProjectOnly(false);
  };

  const hasActiveFilters = selectedCountries.length > 0 || selectedIndustries.length > 0 || linkedCompanyFilter.length > 0 || selectedRegions.length > 0;
  const hasMoreActiveFilters = selectedStages.length > 0 || progressMin !== null || progressMax !== null || valueMin !== null || valueMax !== null || startDateFrom || startDateTo || endDateFrom || endDateTo || masterProjectOnly;
  const moreFiltersCount = [
    selectedStages.length > 0,
    progressMin !== null || progressMax !== null,
    valueMin !== null || valueMax !== null,
    startDateFrom || startDateTo, endDateFrom || endDateTo, masterProjectOnly
  ].filter(Boolean).length;

  // Admin handlers
  const handleCreateProject = () => {
    setEditingProject(null);
    setShowProjectDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteProject) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/projects/${deleteProject._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete project');
      }
      
      toast.success(language === 'ar' ? 'تم حذف المشروع بنجاح' : 'Project deleted successfully');
      fetchProjects(false);
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error(error.message);
    } finally {
      setIsDeleting(false);
      setDeleteProject(null);
    }
  };

  const handleExtractAsLead = async (project) => {
    setSavingLeadId(project._id);
    try {
      const response = await fetch(`${BACKEND_URL}/api/opportunities/from-project/${project._id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to extract lead');

      setSavedLeadIds(prev => new Set(prev).add(project._id));
      if (data.already_in_crm) {
        toast.info(language === 'ar' ? 'هذا المشروع موجود بالفعل في CRM الخاص بك' : 'This project is already in your CRM');
      } else {
        toast.success(language === 'ar' ? 'تمت إضافة المشروع إلى CRM الخاص بك كعميل محتمل' : 'Project added to your CRM as a lead');
        trackFeature('extract_lead', { entity: 'project' });
      }
    } catch (error) {
      console.error('Error extracting lead:', error);
      toast.error(error.message);
    } finally {
      setSavingLeadId(null);
    }
  };


  const formatValue = (valueEstimate) => {
    if (!valueEstimate) return null;
    const { min, max, currency } = valueEstimate;
    // Budgets are stored in MILLIONS of USD (Mill USD). Scale for display.
    const formatNum = (m) => {
      if (m === null || m === undefined || m === '') return null;
      const n = Number(m);
      if (isNaN(n)) return null;
      if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}T`;
      if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}B`;
      return `$${n.toLocaleString()}M`;
    };
    if (min && max) {
      return `${formatNum(min)} - ${formatNum(max)}`;
    }
    const formatted = formatNum(min || max);
    return formatted || '-';
  };

  const translations = {
    en: {
      title: 'Projects',
      subtitle: 'Track mega-projects across the GCC region',
      searchPlaceholder: 'Search projects...',
      allCountries: 'All Countries',
      allStages: 'All Stages',
      allIndustries: 'All Industries',
      concept: 'Concept',
      design: 'Design',
      tender: 'Tender',
      execution: 'Execution',
      complete: 'Completed',
      results: 'projects found',
      noProjects: 'No projects found',
      noProjectsDesc: 'Try adjusting your filters',
      viewDetails: 'View Details',
      estValue: 'Est. Value',
      location: 'Location',
      sector: 'Industry',
      stakeholders: 'Stakeholders',
      refresh: 'Refresh',
      page: 'Page',
      of: 'of',
      trackProject: 'View Project',
      activeFilters: 'Active filters:',
      clearAll: 'Clear all',
      createProject: 'Create Project',
      editProject: 'Edit',
      deleteProject: 'Delete',
      deleteConfirmTitle: 'Delete Project?',
      deleteConfirmDesc: 'This action cannot be undone. This will permanently delete the project.',
      cancel: 'Cancel',
      deleting: 'Deleting...',
      moreFilters: 'More Filters',
      allRegions: 'All Regions',
      progress: 'Progress',
      startDate: 'Start Date',
      endDate: 'End Date',
      masterProject: 'Master Projects',
    },
    ar: {
      title: 'المشاريع',
      subtitle: 'تتبع المشاريع الكبرى في منطقة الخليج',
      searchPlaceholder: 'البحث في المشاريع...',
      allCountries: 'جميع الدول',
      allStages: 'جميع المراحل',
      allIndustries: 'جميع القطاعات',
      concept: 'مفهوم',
      design: 'تصميم',
      tender: 'مناقصة',
      execution: 'تنفيذ',
      complete: 'مكتمل',
      results: 'مشروع',
      noProjects: 'لا توجد مشاريع',
      noProjectsDesc: 'حاول تعديل عوامل التصفية',
      viewDetails: 'عرض التفاصيل',
      estValue: 'القيمة التقديرية',
      location: 'الموقع',
      sector: 'الصناعة',
      stakeholders: 'أصحاب المصلحة',
      refresh: 'تحديث',
      page: 'صفحة',
      of: 'من',
      trackProject: 'عرض المشروع',
      activeFilters: 'الفلاتر النشطة:',
      clearAll: 'مسح الكل',
      createProject: 'إنشاء مشروع',
      editProject: 'تعديل',
      deleteProject: 'حذف',
      deleteConfirmTitle: 'حذف المشروع؟',
      deleteConfirmDesc: 'لا يمكن التراجع عن هذا الإجراء. سيتم حذف المشروع نهائياً.',
      cancel: 'إلغاء',
      deleting: 'جارٍ الحذف...',
      moreFilters: 'المزيد من الفلاتر',
      allRegions: 'جميع المناطق',
      progress: 'التقدم',
      startDate: 'تاريخ البداية',
      endDate: 'تاريخ النهاية',
      masterProject: 'المشاريع الرئيسية',
    }
  };

  const txt = translations[language];
  
  const stageOptions = [
    { value: 'concept', label: txt.concept },
    { value: 'design', label: txt.design },
    { value: 'tender', label: txt.tender },
    { value: 'execution', label: txt.execution },
    { value: 'complete', label: txt.complete }
  ];

  const ProjectCard = ({ project }) => {
    const progress = project.progress ?? stageProgress[project.stage] ?? 0;
    // CRM-converted private project (from a user's opportunity) — render with distinct styling
    const isFromCRM = project.source === 'crm_conversion' && project.visibility === 'private';
    
    return (
      <Card 
        className={`group relative border backdrop-blur-sm card-hover card-glow overflow-hidden cursor-pointer h-full flex flex-col ${
          isFromCRM
            ? 'border-amber-400/60 bg-gradient-to-br from-amber-50 to-amber-100/30 dark:from-amber-950/40 dark:to-amber-900/10 dark:border-amber-500/40 ring-1 ring-amber-300/40'
            : 'border-border/30 bg-card/50'
        }`}
        data-testid={`project-card-${project._id}`}
        onClick={() => navigate(`/dashboard/projects/${project._id}`)}
      >
        <div className="card-hover-highlight" />
        
        {/* Stage-coloured top accent bar */}
        <div className="h-1 w-full bg-muted">
          <div 
            className={`h-full transition-all duration-500 ${
              isFromCRM         ? 'bg-gradient-to-r from-amber-400 to-amber-600'
              : project.stage === 'planning'     ? 'bg-gradient-to-r from-blue-400 to-blue-600'
              : project.stage === 'design'       ? 'bg-gradient-to-r from-violet-400 to-violet-600'
              : project.stage === 'procurement'  ? 'bg-gradient-to-r from-amber-400 to-amber-600'
              : project.stage === 'construction' ? 'bg-gradient-to-r from-orange-400 to-orange-600'
              : project.stage === 'completed'    ? 'bg-gradient-to-r from-emerald-400 to-emerald-600'
              : project.stage === 'on_hold'      ? 'bg-gradient-to-r from-gray-300 to-gray-400'
              : 'bg-gradient-to-r from-primary to-accent'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
        
        <CardContent className="p-6 flex flex-col flex-1">
          {/* Row 1: Header - Ref ID, Country & Stage badges */}
          <div className={`flex items-start justify-between gap-4 mb-4 h-7 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="flex items-center gap-2 flex-wrap">
              {project.ref_id && (
                <Badge className="bg-primary text-primary-foreground font-mono text-xs" data-testid={`project-ref-id-${project._id}`}>
                  {project.ref_id}
                </Badge>
              )}
              <Badge variant="outline" className="flex items-center gap-1 shrink-0">
                <span>{countryFlags[project.country]}</span>
                <span>{countryNames[language][project.country]}</span>
              </Badge>
              {project.is_master_project && (
                <Badge
                  className="bg-blue-600 text-white font-semibold text-[10px] gap-1 shrink-0"
                  data-testid={`project-master-badge-${project._id}`}
                  title={language === 'ar' ? 'مشروع رئيسي' : 'Master Project'}
                >
                  <Building2 className="w-2.5 h-2.5" />
                  {language === 'ar' ? 'رئيسي' : 'Master'}
                </Badge>
              )}
              {isFromCRM && (
                <Badge
                  className="bg-amber-500 hover:bg-amber-500 text-white font-medium text-[10px] gap-1 shrink-0"
                  data-testid={`project-from-crm-${project._id}`}
                  title={language === 'ar' ? 'تم إنشاؤه من CRM — مرئي لك فقط' : 'Created from CRM — visible only to you'}
                >
                  {language === 'ar' ? 'من CRM' : 'From CRM'}
                </Badge>
              )}
            </div>
            <Badge className={`${stageColors[project.stage]} shrink-0 capitalize`}>
              {txt[project.stage]}
            </Badge>
          </div>

          {/* Row 2: Title - Fixed height with line clamp */}
          <div className="h-14 mb-2">
            <h3 className={`text-xl font-bold group-hover:text-primary transition-colors line-clamp-2 ${isRTL ? 'text-right' : ''}`}>
              {project.canonical_name}
            </h3>
          </div>

          {/* Row 4: Location - Fixed height */}
          <div className={`flex items-center gap-2 text-sm h-6 mb-3 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
            <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="truncate">
              {project.city ? `${project.city}, ${countryNames[language][project.country]}` : countryNames[language][project.country]}
            </span>
          </div>

          {/* Row 5: Sector/Industry - Fixed height, shows multiple.
              WO-31 — no row reversal in Arabic. The page already runs right to
              left; reversing the row as well put the list back to front, so the
              clipped row showed the LAST sectors (software, marketing, banking)
              instead of the first (construction, civil works, MEP). */}
          <div className="flex items-center gap-1.5 text-sm h-6 mb-3 overflow-hidden">
            <Briefcase className="w-4 h-4 text-muted-foreground shrink-0" />
            {(() => {
              // WO-73: project sectors only, main first (highlighted), at most 3
              const ids = projectSectorsOf(project);
              if (!ids.length) return <span className="text-muted-foreground">-</span>;
              return ids.map((id, i) => (
                <Badge key={id} variant={i === 0 ? 'secondary' : 'outline'} className="text-[10px] px-1.5 py-0 h-5 shrink-0">
                  {getIndustryLabel(id, language)}
                </Badge>
              ));
            })()}
          </div>

          {/* Row 6: Value - Fixed height */}
          <div className={`flex items-center gap-2 h-6 mb-3 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
            <DollarSign className="w-4 h-4 text-accent shrink-0" />
            <span className="font-bold text-accent">
              {project.value_estimate ? formatValue(project.value_estimate) : '-'}
            </span>
          </div>

          {/* Row 6b: Dates — only shown if at least one date is set */}
          {(project.start_date || project.end_date) && (
            <div className={`flex items-center gap-1.5 text-xs h-5 mb-3 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
              <CalendarDays className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">
                {project.start_date ? formatYear(project.start_date) : '?'}
                <span className="mx-1 opacity-50">→</span>
                {project.end_date ? formatYear(project.end_date) : '?'}
              </span>
              <span className="text-[10px] text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-1 rounded shrink-0">
                {language === 'ar' ? 'تقديري' : 'Tentative'}
              </span>
            </div>
          )}
          {(!project.start_date && !project.end_date) && <div className="h-5 mb-3" />}

          {/* Row 7: Progress - Fixed height */}
          <div className="mb-4">
            <div className={`flex items-center justify-between text-xs mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span className="text-muted-foreground">Progress</span>
              <span className="font-semibold">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Row 8: Stakeholders - Fixed height */}
          <div className={`flex items-center gap-2 text-sm text-muted-foreground h-6 mb-3 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
            <Users className="w-4 h-4 shrink-0" />
            <span>
              {project.stakeholders?.length || 0} {txt.stakeholders}
            </span>
          </div>

          {/* Separator */}
          <div className="border-t border-border/40 mb-3" />

          {/* Linked Counters - News, Tenders & Companies */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <LinkedItemsPopover ids={project.linked_news_ids} type="news" token={token}>
              <div className={`p-2.5 rounded-lg bg-muted/30 border border-border/40 transition-colors ${project.linked_news_ids?.length ? 'cursor-pointer hover:bg-muted/60 hover:border-border/70' : 'cursor-default'} ${isRTL ? 'text-right' : ''}`}>
                <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                  <Newspaper className="w-3.5 h-3.5" />
                  <span className="text-xs">{language === 'ar' ? 'أخبار' : 'news'}</span>
                </div>
                <p className={`text-xl font-bold ${project.linked_news_ids?.length ? 'text-emerald-600' : 'text-blue-900'}`}>{project.linked_news_ids?.length || 0}</p>
              </div>
            </LinkedItemsPopover>
            <LinkedItemsPopover ids={project.linked_tender_ids} type="tenders" token={token}>
              <div className={`p-2.5 rounded-lg bg-muted/30 border border-border/40 transition-colors ${project.linked_tender_ids?.length ? 'cursor-pointer hover:bg-muted/60 hover:border-border/70' : 'cursor-default'} ${isRTL ? 'text-right' : ''}`}>
                <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                  <FileText className="w-3.5 h-3.5" />
                  <span className="text-xs">{language === 'ar' ? 'مناقصات' : 'tenders'}</span>
                </div>
                <p className={`text-xl font-bold ${project.linked_tender_ids?.length ? 'text-emerald-600' : 'text-blue-900'}`}>{project.linked_tender_ids?.length || 0}</p>
              </div>
            </LinkedItemsPopover>
            <LinkedItemsPopover ids={project.linked_company_ids} type="companies" token={token}>
              <div className={`p-2.5 rounded-lg bg-muted/30 border border-border/40 transition-colors ${project.linked_company_ids?.length ? 'cursor-pointer hover:bg-muted/60 hover:border-border/70' : 'cursor-default'} ${isRTL ? 'text-right' : ''}`}>
                <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                  <Users className="w-3.5 h-3.5" />
                  <span className="text-xs">{language === 'ar' ? 'شركات' : 'companies'}</span>
                </div>
                <p className={`text-xl font-bold ${project.linked_company_ids?.length ? 'text-emerald-600' : 'text-blue-900'}`}>{project.linked_company_ids?.length || 0}</p>
              </div>
            </LinkedItemsPopover>
          </div>

          {/* Row 10: Project Location - always reserves space */}
          <div className={`flex items-center gap-2 text-sm h-6 mb-3 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
            {safeUrl(project.location_url) ? (
              <>
                <Navigation className="w-4 h-4 text-blue-500 shrink-0" />
                <a
                  href={safeUrl(project.location_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-700 hover:underline font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  {language === 'ar' ? 'عرض على الخريطة' : 'View on Map'}
                </a>
              </>
            ) : null}
          </div>

          {/* Row 11: Created by */}
          <div className={`flex items-center gap-2 text-xs text-muted-foreground h-5 mb-3 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
            <User className="w-3 h-3 shrink-0" />
            <span>
              {language === 'ar' ? 'أنشئ بواسطة:' : 'Created by:'} {project.created_by_name || (project.created_by === 'admin' ? 'Admin' : project.created_by) || 'Admin'}
            </span>
          </div>

          {/* Row 12: Footer - Always at bottom */}
          <div className={`flex items-center justify-between pt-4 border-t border-border/50 mt-auto ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Private badge on left if present */}
            {project.visibility === 'private' && (
              <Badge variant="outline" className="text-xs text-amber-500 border-amber-500/30">
                <Lock className="w-3 h-3 mr-1" />
                {language === 'ar' ? 'خاص' : 'Private'}
              </Badge>
            )}
            
            <EntityCardActions
              itemId={project._id}
              itemType="project"
              itemTitle={project.name || project.canonical_name}
              shareUrl={project.visibility === 'private' && project.share_token
                ? `${window.location.origin}/shared/project/${project.share_token}`
                : null
              }
              isAdmin={isAdmin}
              canEdit={canEditProject(project)}
              canWatch={user?.plan_id !== 'free'}
              canSaveAsLead={true}
              onSaveAsLead={() => handleExtractAsLead(project)}
              isSavingLead={savingLeadId === project._id}
              savedAsLead={savedLeadIds.has(project._id)}
              onEdit={() => {
                setEditingProject(project);
                setShowProjectDialog(true);
              }}
              onDelete={(isAdmin || (project.created_by && user?.id && project.created_by === user.id)) ? () => setDeleteProject(project) : undefined}
              isFavorite={isFavorite(project._id)}
              onFavoriteToggle={(isFav) => handleFavoriteToggle(project._id, isFav)}
              token={token}
              userPlanId={user?.plan_id}
              language={language}
              isRTL={isRTL}
              primaryAction={() => navigate(`/dashboard/projects/${project._id}`)}
              primaryLabel={txt.trackProject}
              primaryIcon={ArrowRight}
            />
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className={`space-y-6 ${isRTL ? 'font-arabic' : ''}`} data-testid="projects-page">
      <PlanLimitDialog open={planGate.open} onOpenChange={planGate.setOpen} gate={planGate.gate} message={planGate.message} />
      {/* Back to Dashboard Button */}
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
        data-testid="back-to-dashboard-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        {language === 'ar' ? 'العودة إلى لوحة التحكم' : 'Back to Dashboard'}
      </Button>

      {/* Header */}
      <div className={`flex flex-col md:flex-row md:items-center justify-between gap-4 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : ''}>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 shadow-lg">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            {txt.title}
          </h1>
          <p className="text-muted-foreground mt-2">{txt.subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-wo40="header-actions">
          {/* Export Button - Only for paid users */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                disabled={isExporting}
                data-testid="export-projects-btn"
              >
                <Download className="w-4 h-4" />
                {isExporting 
                  ? (language === 'ar' ? 'جاري التصدير...' : 'Exporting...') 
                  : (language === 'ar' ? 'تصدير' : 'Export')}
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('json')} className="cursor-pointer">
                <FileJson className="w-4 h-4 mr-2" />
                JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')} className="cursor-pointer">
                <FileText className="w-4 h-4 mr-2" />
                CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('excel')} className="cursor-pointer">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {canCreateProjects && (
            <Button 
              onClick={handleCreateProject}
              className="flex items-center gap-2 bg-primary hover:bg-primary/90"
              data-testid="create-project-btn"
            >
              <Plus className="w-4 h-4" />
              {txt.createProject}
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={() => fetchProjects(false)}
            className="flex items-center gap-2"
            data-testid="refresh-projects-btn"
          >
            <RefreshCw className="w-4 h-4" />
            {txt.refresh}
          </Button>
          {/* View mode toggle */}
          <div className="flex items-center border rounded-md overflow-hidden">
            <Button
              variant={viewMode === 'card' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none h-9 px-3"
              onClick={() => setViewMode('card')}
              data-testid="projects-view-card-btn"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none h-9 px-3"
              onClick={() => setViewMode('list')}
              data-testid="projects-view-list-btn"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-0 bg-card/50 backdrop-blur-sm">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className={`m2b-mfilters flex flex-col md:flex-row gap-3 flex-wrap ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            <div className="m2b-keep relative flex-1 min-w-[200px]">
              <Search className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
              <Input
                type="text"
                placeholder={txt.searchPlaceholder}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={`${isRTL ? 'pr-10' : 'pl-10'} h-11`}
                data-testid="projects-search-input"
              />
            </div>
            {/* WO-40 — phones: filters fold away behind this button (CSS in index.css, .m2b-mfilters) */}
            <label className="m2b-keep m2b-ftoggle md:hidden flex items-center justify-between h-11 px-4 border rounded-lg bg-background text-sm font-medium cursor-pointer select-none" data-testid="projects-filters-toggle">
              <input type="checkbox" className="sr-only" aria-label={language === 'ar' ? 'إظهار عوامل التصفية' : 'Show filters'} />
              <span>{language === 'ar' ? 'عوامل التصفية' : 'Filters'}</span>
              <span className="m2b-ftoggle-arrow" aria-hidden="true">▾</span>
            </label>
            
            {/* Multi-select Country Filter */}
            <MultiSelectFilter
              label={txt.allCountries}
              icon={Globe}
              options={Object.keys(countryFlags).map(code => ({
                value: code,
                label: countryNames[language][code],
                icon: countryFlags[code],
                locked: isCountryLocked(code)
              }))}
              onLockedClick={handleLockedCountry}
              selected={selectedCountries}
              onChange={(values) => { setSelectedCountries(values); }}
              language={language}
              isRTL={isRTL}
              testId="projects-country-filter"
            />

            {/* Region filter — enabled only when countries are selected */}
            <MultiSelectFilter
              label={txt.allRegions}
              icon={MapPin}
              options={availableRegionOptions}
              selected={selectedRegions}
              onChange={setSelectedRegions}
              language={language}
              isRTL={isRTL}
              testId="projects-region-filter"
              disabled={selectedCountries.length === 0}
              disabledHint={language === 'ar' ? 'اختر دولة أولاً' : 'Select a country first'}
            />
            
            {/* Multi-select Industry Filter (grouped by category) */}
            <IndustryMultiSelectFilter
              label={txt.allIndustries}
              icon={Factory}
              selected={selectedIndustries}
              onChange={(values) => { setSelectedIndustries(values); }}
              language={language}
              isRTL={isRTL}
              scope="projects"
              testId="projects-industry-filter"
            />

            {/* Filter by linked Company */}
            <LinkedEntityFilter
              entityType="companies"
              selected={linkedCompanyFilter}
              onChange={(val) => { setLinkedCompanyFilter(val); setPage(1); }}
              language={language}
              token={token}
              testId="projects-company-filter"
            />
            
            {/* More Filters Toggle */}
            <Button
              type="button"
              variant="outline"
              className={`h-11 px-3 gap-2 ${(showMoreFilters || hasMoreActiveFilters) ? 'border-primary text-primary' : ''}`}
              onClick={() => setShowMoreFilters(v => !v)}
              data-testid="more-filters-toggle"
            >
              <SlidersHorizontal className="w-4 h-4" />
              {txt.moreFilters}
              {moreFiltersCount > 0 && (
                <Badge className="h-5 px-1.5 text-xs bg-primary text-primary-foreground ml-1">
                  {moreFiltersCount}
                </Badge>
              )}
              <ChevronDown className={`w-4 h-4 transition-transform ${showMoreFilters ? 'rotate-180' : ''}`} />
            </Button>

            {/* Favorites Toggle */}
            <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-background">
              <Heart className={`w-4 h-4 ${showFavoritesOnly ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`} />
              <span className="text-sm whitespace-nowrap">
                {language === 'ar' ? 'المفضلة فقط' : 'Favorites only'}
              </span>
              <Switch
                checked={showFavoritesOnly}
                onCheckedChange={setShowFavoritesOnly}
                data-testid="projects-favorites-toggle"
              />
            </div>

            <Button type="submit" className="m2b-keep bg-primary hover:bg-primary/90 h-11" data-testid="projects-search-btn">
              <Search className="w-4 h-4 mr-2" />
              {language === 'ar' ? 'بحث' : 'Search'}
            </Button>
          </form>

          {/* Expanded "More Filters" panel */}
          {showMoreFilters && (
            <div className={`mt-3 pt-3 border-t border-border/50 flex flex-wrap gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {/* Stage filter */}
              <MultiSelectFilter
                label={txt.allStages}
                icon={TrendingUp}
                options={stageOptions}
                selected={selectedStages}
                onChange={(values) => { setSelectedStages(values); }}
                language={language}
                isRTL={isRTL}
                testId="projects-stage-filter"
              />

              {/* Progress range */}
              <ProgressRangeFilter
                minVal={progressMin}
                maxVal={progressMax}
                onChange={(min, max) => { setProgressMin(min); setProgressMax(max); }}
                language={language}
                testId="projects-progress-filter"
              />

              {/* Estimated Value range */}
              <ValueRangeFilter
                minVal={valueMin}
                maxVal={valueMax}
                onChange={(min, max) => { setValueMin(min); setValueMax(max); }}
                language={language}
                testId="projects-value-filter"
              />

              {/* Start Date range */}
              <DateRangeFilter
                label={txt.startDate}
                icon={CalendarDays}
                fromVal={startDateFrom}
                toVal={startDateTo}
                onChange={(from, to) => { setStartDateFrom(from); setStartDateTo(to); }}
                language={language}
                testId="projects-start-date-filter"
              />

              {/* End Date range */}
              <DateRangeFilter
                label={txt.endDate}
                icon={CalendarDays}
                fromVal={endDateFrom}
                toVal={endDateTo}
                onChange={(from, to) => { setEndDateFrom(from); setEndDateTo(to); }}
                language={language}
                testId="projects-end-date-filter"
              />

              {/* Master Project toggle */}
              <button
                type="button"
                onClick={() => setMasterProjectOnly(v => !v)}
                className={`h-11 px-4 rounded-md border text-sm font-medium flex items-center gap-2 transition-colors ${
                  masterProjectOnly
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-background border-input hover:bg-muted text-foreground'
                }`}
                data-testid="projects-master-filter"
              >
                <Building2 className="w-4 h-4" />
                {txt.masterProject}
              </button>
            </div>
          )}
          
          {/* Active filters display */}
          {(hasActiveFilters || hasMoreActiveFilters) && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50 flex-wrap">
              <span className="text-sm text-muted-foreground">{txt.activeFilters}</span>
              {selectedCountries.map(code => (
                <Badge key={code} variant="secondary" className="gap-1">
                  {countryFlags[code]} {countryNames[language][code]}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedCountries(prev => prev.filter(c => c !== code))} />
                </Badge>
              ))}
              {selectedStages.map(stage => (
                <Badge key={stage} variant="secondary" className="gap-1">
                  <TrendingUp className="w-3 h-3" /> {txt[stage]}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedStages(prev => prev.filter(s => s !== stage))} />
                </Badge>
              ))}
              {selectedIndustries.map(industry => (
                <Badge key={industry} variant="secondary" className="gap-1">
                  <Factory className="w-3 h-3" /> {getIndustryLabel(industry, language)}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedIndustries(prev => prev.filter(i => i !== industry))} />
                </Badge>
              ))}
              {linkedCompanyFilter.map(f => (
                <Badge key={f._id} variant="secondary" className="gap-1 border-primary/30 bg-primary/10 text-primary">
                  <Building2 className="w-3 h-3" /> {f.name}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setLinkedCompanyFilter(prev => prev.filter(x => x._id !== f._id))} />
                </Badge>
              ))}
              {selectedRegions.map(r => (
                <Badge key={r} variant="secondary" className="gap-1 border-orange-400/30 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400">
                  <MapPin className="w-3 h-3" /> {r}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedRegions(prev => prev.filter(x => x !== r))} />
                </Badge>
              ))}
              {(progressMin !== null || progressMax !== null) && (
                <Badge variant="secondary" className="gap-1 border-teal-400/30 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400">
                  <TrendingUp className="w-3 h-3" />
                  {progressMin !== null && progressMax !== null ? `${progressMin}%–${progressMax}%` : progressMin !== null ? `≥${progressMin}%` : `≤${progressMax}%`}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => { setProgressMin(null); setProgressMax(null); }} />
                </Badge>
              )}
              {(valueMin !== null || valueMax !== null) && (
                <Badge variant="secondary" className="gap-1 border-emerald-400/30 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400">
                  <DollarSign className="w-3 h-3" />
                  {(() => {
                    const fmt = (n) => `$${(n / 1_000_000).toLocaleString()}M`;
                    if (valueMin != null && valueMax != null) return `${fmt(valueMin)} – ${fmt(valueMax)}`;
                    if (valueMin != null) return `> ${fmt(valueMin)}`;
                    return `< ${fmt(valueMax)}`;
                  })()}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => { setValueMin(null); setValueMax(null); }} />
                </Badge>
              )}
              {(startDateFrom || startDateTo) && (
                <Badge variant="secondary" className="gap-1 border-blue-400/30 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
                  <CalendarDays className="w-3 h-3" />
                  {language === 'ar' ? 'البداية:' : 'Start:'} {startDateFrom || '?'} – {startDateTo || '?'}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => { setStartDateFrom(null); setStartDateTo(null); }} />
                </Badge>
              )}
              {(endDateFrom || endDateTo) && (
                <Badge variant="secondary" className="gap-1 border-violet-400/30 bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400">
                  <CalendarDays className="w-3 h-3" />
                  {language === 'ar' ? 'النهاية:' : 'End:'} {endDateFrom || '?'} – {endDateTo || '?'}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => { setEndDateFrom(null); setEndDateTo(null); }} />
                </Badge>
              )}
              {masterProjectOnly && (
                <Badge variant="secondary" className="gap-1 border-blue-600/30 bg-blue-600/10 text-blue-600">
                  <Building2 className="w-3 h-3" /> {txt.masterProject}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setMasterProjectOnly(false)} />
                </Badge>
              )}
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs h-7">
                {txt.clearAll}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results count */}
      {!loading && (
        <div className={`text-sm text-muted-foreground ${isRTL ? 'text-right' : ''}`}>
          {language === 'ar' 
            ? `عرض ${displayedProjects.length} من ${total} مشروع${showFavoritesOnly ? ' (المفضلة)' : ''}` 
            : `Showing ${displayedProjects.length} of ${total} projects${showFavoritesOnly ? ' (Favorites)' : ''}`
          }
        </div>
      )}

      {/* Linked filter banner */}
      {isFiltered && !loading && (
        <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 text-sm">
          <span className="font-medium text-primary">
            Showing {filterIds.length} linked project{filterIds.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={() => navigate('/dashboard/projects')}
            className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            View all projects <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="border-0 bg-card/50">
              <div className="h-1 w-full bg-muted" />
              <CardContent className="p-6 space-y-4">
                <div className="flex justify-between">
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-6 w-20" />
                </div>
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-4 w-2/3" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-36" />
                </div>
                <Skeleton className="h-2 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card className="border-0 bg-destructive/10">
          <CardContent className="p-8 text-center">
            <p className="text-destructive">{error}</p>
            <Button onClick={fetchProjects} variant="outline" className="mt-4">
              {language === 'ar' ? 'إعادة المحاولة' : 'Try Again'}
            </Button>
          </CardContent>
        </Card>
      ) : displayedProjects.length === 0 ? (
        <Card className="border-0 bg-card/50">
          <div className="p-12 text-center">
            <Building2 className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            {countryGate ? (
              <>
                <h3 className="text-xl font-semibold mb-2">{language === 'ar' ? 'سوق مقفل' : 'Locked market'}</h3>
                <p className="text-muted-foreground mb-4">{countryGate.message}</p>
                <Button data-testid="country-gate-upgrade-btn" onClick={() => { window.location.href = countryGate.upgrade_url || '/dashboard/settings?tab=plans'; }}>
                  {language === 'ar' ? 'ترقية الخطة' : 'Upgrade plan'}
                </Button>
              </>
            ) : (
              <OnboardingEmptyState kind="noResults" variant="inline" when={hasActiveFilters || !!search} onClear={() => { clearAllFilters(); setSearch(''); setSearchInput(''); }}>
              <>
                <h3 className="text-xl font-semibold mb-2">{txt.noProjects}</h3>
                <p className="text-muted-foreground">{txt.noProjectsDesc}</p>
              </>
              </OnboardingEmptyState>
            )}
          </div>
        </Card>
      ) : viewMode === 'list' ? (
        /* ─── List / Table view ─── */
        <Card className="border-0 bg-card/50 backdrop-blur-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="font-semibold">{language === 'ar' ? 'المشروع' : 'Project'}</TableHead>
                <TableHead className="font-semibold">{language === 'ar' ? 'الدولة' : 'Country'}</TableHead>
                <TableHead className="font-semibold">{language === 'ar' ? 'الصناعة' : 'Industry'}</TableHead>
                <TableHead className="font-semibold">{language === 'ar' ? 'القيمة' : 'Value'}</TableHead>
                <TableHead className="font-semibold">{language === 'ar' ? 'المرحلة' : 'Stage'}</TableHead>
                <TableHead className="font-semibold">{language === 'ar' ? 'التقدم' : 'Progress'}</TableHead>
                <TableHead className="font-semibold">{language === 'ar' ? 'الجدول الزمني' : 'Timeline'}</TableHead>
                <TableHead className="font-semibold text-center">{language === 'ar' ? 'الروابط' : 'Links'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedProjects.map((project) => {
                const progress = project.progress ?? stageProgress[project.stage] ?? 0;
                const stageColor = {
                  concept:'bg-purple-100 text-purple-700', design:'bg-blue-100 text-blue-700',
                  tender:'bg-amber-100 text-amber-700', execution:'bg-emerald-100 text-emerald-700',
                  complete:'bg-gray-100 text-gray-500'
                }[project.stage] || 'bg-muted text-muted-foreground';
                const sector = projectSectorsOf(project).map(id => getIndustryLabel(id, language)).join(', ') || '—'; // WO-73
                const startYear = project.start_date ? project.start_date.slice(0, 4) : null;
                const endYear = project.end_date ? project.end_date.slice(0, 4) : null;
                return (
                  <TableRow
                    key={project._id}
                    className="cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => navigate(`/dashboard/projects/${project._id}`)}
                    data-testid={`project-list-row-${project._id}`}
                  >
                    <TableCell>
                      {project.ref_id
                        ? <span className="font-mono text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">{project.ref_id}</span>
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{countryFlags[project.country]} {countryNames[language][project.country]}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground truncate max-w-[120px] block">{sector}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium">{project.value_estimate ? formatValue(project.value_estimate) : '—'}</span>
                    </TableCell>
                    <TableCell>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${stageColor}`}>
                        {txt[project.stage] || project.stage || '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-semibold">{progress}%</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {startYear && endYear ? `${startYear} – ${endYear}` : startYear || endYear || '—'}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
                        <span className={project.linked_news_ids?.length ? 'text-emerald-600 font-bold' : 'text-blue-900 font-bold'}>{project.linked_news_ids?.length || 0}</span>
                        <span className={project.linked_tender_ids?.length ? 'text-emerald-600 font-bold' : 'text-blue-900 font-bold'}>{project.linked_tender_ids?.length || 0}</span>
                        <span className={project.linked_company_ids?.length ? 'text-emerald-600 font-bold' : 'text-blue-900 font-bold'}>{project.linked_company_ids?.length || 0}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
          {displayedProjects.map((project) => (
            <ProjectCard key={project._id} project={project} />
          ))}
        </div>
      )}

      {/* Load More Button */}
      {hasMore && displayedProjects.length > 0 && (
        <div className="flex justify-center pt-8">
          <Button
            variant="outline"
            size="lg"
            onClick={loadMore}
            disabled={loadingMore}
            className="min-w-[200px]"
            data-testid="load-more-projects"
          >
            {loadingMore ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-2" />
                {language === 'ar' ? 'تحميل المزيد من المشاريع' : 'Load More Projects'}
              </>
            )}
          </Button>
        </div>
      )}
      
      {/* No more items message */}
      {!hasMore && displayedProjects.length > 0 && (
        <div className="text-center pt-8 text-muted-foreground">
          <p>{language === 'ar' ? 'لا يوجد المزيد من المشاريع' : 'No more projects to load'}</p>
        </div>
      )}
      
      {/* Admin Project Dialog */}
      <AdminProjectDialog
        open={showProjectDialog}
        onOpenChange={setShowProjectDialog}
        project={editingProject}
        token={token}
        language={language}
        onSuccess={() => fetchProjects(false)}
        userPlan={user?.plan_id || 'free'}
        onPlanGate={(pg) => planGate.show(pg)}
      />
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteProject} onOpenChange={(open) => !open && setDeleteProject(null)}>
        <AlertDialogContent data-testid="delete-project-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{txt.deleteConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {txt.deleteConfirmDesc}
              {deleteProject && (
                <span className="block mt-2 font-semibold text-foreground">
                  "{deleteProject.canonical_name}"
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting} data-testid="cancel-delete-project">
              {txt.cancel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-project"
            >
              {isDeleting ? txt.deleting : txt.deleteProject}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
